import {containsCssCustomValue} from '@packages/systems/style/raw-values';
import {get, trim, each, endsWith, reject, map} from 'lodash';

import {StylePath} from '@packages/systems/style-legacy/values';
import {
  computedValue,
  computedUnits,
} from '@packages/shared/stores/StyleSystem/renderers';
import * as CssUtils from '@packages/systems/style-legacy/utils';
import * as StringUtils from '@packages/utilities/string-utils';
import * as StreamTypes from '../constants/StreamTypes';
import * as StyleVariables from '@packages/systems/core/utils/StyleVariables';
import type {Plugins} from '@packages/systems/wfdl';

import {
  sortCssProperties,
  CSS_PROPERTY_DETAILS,
} from '@packages/systems/style-legacy/css-properties';
import {cssPropFromPath} from './CssPropertyUtils';
import {isCssVariableProperty} from '@packages/systems/core/utils/CssStringUtils';
import {
  renderCssVariableName,
  variableDefinitionPlaceholder,
} from '@packages/systems/core/utils/StyleVariables';
import {once} from '@packages/utilities/memo';

import type {
  StyleStateType,
  StyleProperty,
  StyleStore$ValueType,
  BackgroundImage,
  BoxShadow,
  TextShadow,
  EffectFilter,
  EffectTransform,
  EffectTransition,
  ColorStop,
} from '@packages/systems/style-legacy/values';

const getLocal = <T extends StyleStore$ValueType>(
  value: StyleProperty<T>
): {value: T['value']; unit: T['unit']} | null => {
  const v = value.local.value;

  if (v !== null) {
    const u = value.local.unit;

    return {
      value: v,
      unit: u,
    };
  } else {
    return null;
  }
};

const combinedPosition = <T extends StyleStore$ValueType>(
  value: StyleProperty<T>
) => {
  const v = computedValue(value);

  if (v === null) {
    return null;
  } else {
    const u = computedUnits(value);
    return String(v) + (u === null ? '' : u);
  }
};

const TRANSFORM_PARTS = [
  'rotateX',
  'rotateY',
  'rotateZ',
  'translateX',
  'translateY',
  'translateZ',
  'scaleX',
  'scaleY',
  'scaleZ',
  'skewX',
  'skewY',
  'perspective',
] as const;

const FILTER_PARTS = [
  'blur',
  'dropShadow',
  'hueRotate',
  'saturate',
  'brightness',
  'contrast',
  'grayscale',
  'sepia',
  'invert',
] as const;

const layoutSection = [
  'display',
  'position',
  'verticalAlign',
  'float',
  'clear',
  'visibility',

  'flexDirection',
  'justifyContent',
  'flexWrap',
  'alignItems',
  'justifyItems',
  'alignContent',
  'alignSelf',
  'order',
  'flexGrow',
  'flexShrink',
  'flexBasis',

  'gridAutoFlow',
  'gridAutoColumns',
  'gridAutoRows',
  {property: 'gridColumnGap', formatter: ({value}: {value: any}) => value},
  {property: 'gridRowGap', formatter: ({value}: {value: any}) => value},
  {
    property: 'gridTemplateColumns',
    formatter: ({value}: {value: any}) => value,
  },
  {property: 'gridTemplateRows', formatter: ({value}: {value: any}) => value},
  'gridTemplateAreas',

  'aspectRatio',
  'boxSizing',

  'marginTop',
  'marginRight',
  'marginBottom',
  'marginLeft',

  'paddingTop',
  'paddingRight',
  'paddingBottom',
  'paddingLeft',

  'objectFit',
  'objectPosition',
  'overflow',

  'width',
  'minWidth',
  'maxWidth',

  'height',
  'minHeight',
  'maxHeight',

  'top',
  'right',
  'bottom',
  'left',

  'whiteSpace',

  'zIndex',
];

const typographySection = [
  'fontFamily',
  {
    property: 'fontVariationSettings',
    formatter: (localBaseValue: {value: any}) =>
      formatFontVariationSettingParts(localBaseValue.value),
  },
  'fontWeight',
  'fontStyle',
  'fontColor',
  'fontSize',
  'lineHeight',
  'letterSpacing',
  'textIndent',
  'textAlign',
  // 'verticalAlign',
  'textTransform',
  'textDecoration',
  'textDecorationColor',
  'textUnderlineOffset',
  'textDecorationThickness',
  'textUnderlinePosition',
  'textDecorationSkipInk',
  'listStyleType',
  'columnCount',
  'columnGap',
  'columnRuleColor',
  'columnRuleStyle',
  'columnRuleWidth',
  'columnSpan',
  'direction',
  'backgroundClip',
  'textOverflow',
  'textStrokeWidth',
  'textStrokeColor',
  'wordBreak',
  'overflowWrap',
];

const backgroundSection = [
  'backgroundColor',
  {
    property: 'backgroundImages',
    formatter: function (localBaseValue: {value: any}) {
      return formatBackgroundParts(localBaseValue.value);
    },
    multi: true,
  },
];

const borderSection = [
  'borderTopStyle',
  'borderTopWidth',
  'borderTopColor',
  'borderRightStyle',
  'borderRightWidth',
  'borderRightColor',
  'borderBottomStyle',
  'borderBottomWidth',
  'borderBottomColor',
  'borderLeftStyle',
  'borderLeftWidth',
  'borderLeftColor',
  'borderRadiusTopLeft',
  'borderRadiusTopRight',
  'borderRadiusBottomLeft',
  'borderRadiusBottomRight',
];

const shadowsSection = [
  {
    property: 'boxShadows',
    formatter(localBaseValue: {value: any}) {
      return formatShadowParts(localBaseValue.value);
    },
  },
  {
    property: 'textShadows',
    formatter(localBaseValue: {value: any}) {
      return formatShadowParts(localBaseValue.value);
    },
  },
];

const effectsSection = [
  'mixBlendMode',
  'outlineColor',
  'outlineOffset',
  'outlineWidth',
  'outlineStyle',
  {
    property: 'opacity',
    formatter(localBaseValue: {value: any}) {
      // opacity is unitful in the UI but unitless in the CSS
      return localBaseValue.value === null ? '' : String(localBaseValue.value);
    },
  },
  'cursor',
  'pointerEvents',
  {
    property: 'transitions',
    formatter(localBaseValue: {value: any}) {
      return formatTransitionParts(localBaseValue.value);
    },
    multi: true,
  },
  {
    property: 'transforms',
    formatter(localBaseValue: {value: any}) {
      return formatTransformParts(localBaseValue.value);
    },
  },
  {
    property: 'filters',
    formatter(localBaseValue: {value: any}) {
      return formatFilterParts(localBaseValue.value);
    },
  },
  {
    property: 'backdropFilters',
    formatter(localBaseValue: {value: any}) {
      return formatFilterParts(localBaseValue.value);
    },
  },
];

const transitionsAndTransforms = [
  {
    property: 'perspective',
    // Chrome 93 changes the behavior of `perspective: 0`.
    // https://groups.google.com/u/1/a/chromium.org/g/blink-dev/c/Up0ruPbddro/m/7J-f9pqiAQAJ
    // Previously `perspective: 0` would act as if it didn't exist due to
    // it being resulting in an identity matrix transformation.
    // Chrome 93+ treats `perspective: 0` as a clamp to `perspective: 1`.
    // To prevent this, we are treating `perspective: 0` as `prespective: none`.
    formatter(localBaseValue: {value: any}) {
      return typeof localBaseValue.value === 'number' &&
        localBaseValue.value < 1
        ? 'none'
        : localBaseValue;
    },
  },
  'perspectiveOrigin',
  'transformOrigin',
  'backfaceVisibility',
];
type LessPropEntry =
  | string
  | {
      property: string;
      formatter: (localBaseValue: {value: any}) => any;
      multi?: boolean;
    };

type LessProp = {
  p: string | undefined;
  value: any;
};

const {properties} = [
  ...layoutSection,
  ...typographySection,
  ...backgroundSection,
  ...borderSection,
  ...shadowsSection,
  ...effectsSection,
  ...transitionsAndTransforms,

  ...Object.values(CSS_PROPERTY_DETAILS).map((p) => p.path),
].reduce<{properties: LessPropEntry[]; seen: Set<string>}>(
  (acc, prop) => {
    const propName = typeof prop === 'object' ? prop.property : prop;
    if (acc.seen.has(propName)) return acc;
    acc.properties.push(prop);
    acc.seen.add(propName);

    return acc;
  },
  {properties: [], seen: new Set()}
);

export function formatLess(
  state: StyleStateType,
  // TODO: These are temporarily optional; in a follow-up we will make these
  // required once all existing callsites are updated:
  variables: StyleVariables.VariableMap = StyleVariables.emptyVariableMap,
  variableCollections: StyleVariables.VariableCollectionMap = StyleVariables.emptyVariableCollectionMap,
  slugMap: Plugins.PluginsType['slugMap'] = {}
) {
  function statePath(path: string) {
    return get(state, StylePath.expandPath(path));
  }

  function statePathLocalGet(path: string) {
    try {
      return getLocal(statePath(path));
    } catch (err) {
      console.log('Failed to get information for %s', path, statePath(path));
    }
  }

  const lessProps: LessProp[] = [];

  function pushLessProp(property: string | undefined, value: any) {
    lessProps.push({
      p: property,
      value,
    });
  }

  const getVariablesByName = once(() => {
    const variablesByName = new Map();
    for (const variable of variables) {
      variablesByName.set(
        `--${renderCssVariableName(
          variables,
          variableCollections,
          slugMap,
          variable.id
        )}`,
        variable
      );
    }
    return variablesByName;
  });

  const replaceCssVariablesWithPlaceholders = (
    property: string | undefined
  ) => {
    if (property == null || !isCssVariableProperty(property)) return property;
    const variablesByName = getVariablesByName();
    const matchingVariable = variablesByName.get(property);
    if (!matchingVariable) return property;
    return variableDefinitionPlaceholder(matchingVariable.id);
  };

  (
    [
      // Standard browser properties
      ...properties,
      // CSS Variables
      ...Object.keys(state).filter(isCssVariableProperty),
    ] as LessPropEntry[]
  ).forEach(function (property) {
    if (typeof property === 'string') {
      const localBaseValue = statePathLocalGet(property);

      if (localBaseValue !== null) {
        pushLessProp(
          replaceCssVariablesWithPlaceholders(cssPropFromPath(property)),
          localBaseValue
        );
      }
    } else if (typeof property === 'object') {
      const path = property.property;
      const localBaseValue = statePathLocalGet(path);

      if (localBaseValue !== null) {
        const formattedValue = property.formatter(
          localBaseValue as {value: any}
        );

        if (!property.multi) {
          if (formattedValue !== null) {
            pushLessProp(
              replaceCssVariablesWithPlaceholders(cssPropFromPath(path)),
              formattedValue
            );
          }
        } else {
          Array.isArray(formattedValue) &&
            formattedValue.forEach(function (value: {
              property: string;
              value: any;
            }) {
              if (value.value !== null) {
                pushLessProp(value.property, value.value);
              }
            });
        }
      }
    }
  });

  // Sort properties by logical order
  sortCssProperties(
    lessProps,
    'p',
    (prop) =>
      typeof prop.value.value === 'string' &&
      containsCssCustomValue(prop.value.value)
  );

  const buf: string[] = [];
  lessProps.forEach(function (prop) {
    buf.push(prop.p + ': ' + CssUtils.renderValue(prop.value) + ';');
  });

  return buf.join(' ');
}

type FilterPart = (typeof FILTER_PARTS)[number];

function formatFilterParts(values: EffectFilter[]) {
  if (values.length > 0) {
    return _collapseValue({
      values,
      formatter: function (value, stream) {
        const localValues = FILTER_PARTS.reduce<
          Record<FilterPart, string | null>
        >(
          (obj, part) => {
            obj[part] = CssUtils.renderValue({
              value: value[part][stream].value,
              unit: value[part][stream].unit,
            });
            return obj;
          },
          {} as Record<FilterPart, string | null>
        );

        // Update the part's cached CSS value
        if (FILTER_PARTS.every((part) => localValues[part] === null)) {
          return null;
        } else {
          let joinedCss = null;

          if (localValues.blur !== null) {
            joinedCss = 'blur(' + localValues.blur + ')';
          } else if (localValues.hueRotate !== null) {
            joinedCss = 'hue-rotate(' + localValues.hueRotate + ')';
          } else if (localValues.saturate !== null) {
            joinedCss = 'saturate(' + localValues.saturate + ')';
          } else if (localValues.brightness !== null) {
            joinedCss = 'brightness(' + localValues.brightness + ')';
          } else if (localValues.contrast !== null) {
            joinedCss = 'contrast(' + localValues.contrast + ')';
          } else if (localValues.grayscale !== null) {
            joinedCss = 'grayscale(' + localValues.grayscale + ')';
          } else if (localValues.sepia !== null) {
            joinedCss = 'sepia(' + localValues.sepia + ')';
          } else if (localValues.invert !== null) {
            joinedCss = 'invert(' + localValues.invert + ')';
          } else if (localValues.dropShadow !== null) {
            joinedCss = 'drop-shadow(' + localValues.dropShadow + ')';
          }
          return joinedCss;
        }
      },
      cssPartSeparator: ' ',
      defaultEmptyValue: 'none',
    });
  } else {
    return 'none';
  }
}

function formatFontVariationSettingParts(values: any[]) {
  if (values.length === 0) return 'normal';

  return _collapseValue({
    values,
    formatter: function (value, stream) {
      for (const part in value) {
        //  _guid,  _type, and hidden are metadata and not an actual variation tag
        if (part === '_guid' || part === '_type' || part === 'hidden') {
          continue;
        }

        const axisValue = CssUtils.renderValue({
          value: value[part][stream].value,
          unit: value[part][stream].unit,
        });

        if (axisValue != null) return `"${part}" ${axisValue}`;
      }

      return null;
    },
    cssPartSeparator: ', ',
    defaultEmptyValue: 'normal',
  });
}

function formatBackgroundParts(values: BackgroundImage[]) {
  const CSS_BACKGROUND = 'background';
  const CSS_IMAGE = CSS_BACKGROUND + '-image';
  const CSS_SIZE = CSS_BACKGROUND + '-size';
  const CSS_POSITION = CSS_BACKGROUND + '-position';
  const CSS_REPEAT = CSS_BACKGROUND + '-repeat';
  const CSS_ATTACHMENT = CSS_BACKGROUND + '-attachment';

  function repeatFormatter(value: any, stream: StreamType) {
    if (value._type === 'background') {
      return _deepFormatter('repeat')(value, stream);
    } else {
      return null;
    }
  }

  function attachmentFormatter(value: any, stream: StreamType) {
    if (value._type === 'background') {
      return _deepFormatter('attachment')(value, stream);
    } else {
      return null;
    }
  }

  function sizeFormatter(value: any, stream: StreamType) {
    if (value._type === 'background') {
      const widthValue = value.width[stream].value;
      const heightValue = value.height[stream].value;

      if (widthValue !== null || heightValue !== null) {
        const widthUnit = value.width[stream].unit;
        const heightUnit = value.height[stream].unit;

        if (widthValue !== null || heightValue !== null) {
          if (widthValue === 'cover' && heightValue === 'cover') {
            return 'cover';
          } else if (widthValue === 'contain' && heightValue === 'contain') {
            return 'contain';
          } else {
            const widthCss =
              typeof widthValue === 'number' ? widthValue + widthUnit : 'auto';
            const heightCss =
              typeof heightValue === 'number' ? heightValue + heightUnit : '';
            const heightWidthCss =
              widthCss + (heightCss ? ' ' + heightCss : '');
            return heightWidthCss;
          }
        } else {
          return 'auto';
        }
      } else {
        return null;
      }
    } else {
      return null;
    }
  }

  function positionFormatter(value: any, stream: StreamType) {
    if (value._type === 'background') {
      const leftValue = value.left[stream].value;
      const topValue = value.top[stream].value;

      if (leftValue !== null || topValue !== null) {
        const leftUnit = value.left[stream].unit || 'px';
        const topUnit = value.top[stream].unit || 'px';

        const leftCss =
          leftValue !== null
            ? leftValue + (typeof leftValue === 'number' ? leftUnit : '')
            : 0 + topUnit;
        const topCss =
          topValue !== null
            ? topValue + (typeof topValue === 'number' ? topUnit : '')
            : 0 + leftUnit;

        const css = leftCss + (topCss ? ' ' + topCss : '');
        return css;
      } else {
        return null;
      }
    } else {
      return null;
    }
  }

  if (values.length > 0) {
    const result = [
      {
        path: null,
        cssProp: CSS_IMAGE,
        reset: 'none',
        formatter: formatBackgroundPart,
      },
      {
        path: 'repeat',
        cssProp: CSS_REPEAT,
        reset: 'repeat',
        formatter: repeatFormatter,
      },
      {
        path: 'attachment',
        cssProp: CSS_ATTACHMENT,
        reset: 'scroll',
        formatter: attachmentFormatter,
      },
      {
        path: 'size',
        cssProp: CSS_SIZE,
        reset: 'auto',
        formatter: sizeFormatter,
      },
      {
        path: 'position',
        cssProp: CSS_POSITION,
        reset: 0 + 'px ' + (0 + 'px'),
        formatter: positionFormatter,
      },
    ].map(function (subvalue) {
      return {
        property: subvalue.cssProp,
        value: _collapseValue({
          values,
          formatter: subvalue.formatter,
          defaultEmptyValue: subvalue.reset,
        }),
      };
    });

    return result;
  } else {
    return [
      {
        property: CSS_IMAGE,
        value: 'none',
      },
    ];
  }
}

export function formatShadowParts(values: (BoxShadow | TextShadow)[]) {
  if (values.length > 0) {
    return _collapseValue({
      values,
      formatter: function (value, stream) {
        const localX = value.x[stream].value;
        const localY = value.y[stream].value;
        const localBlur = CssUtils.renderValue({
          value: value.blur[stream].value,
          unit: value.blur[stream].unit,
        });
        const localSpread = CssUtils.renderValue({
          value: value.spread[stream].value,
          unit: value.spread[stream].unit,
        });
        const localPosition = value.position[stream].value;
        const localColor = value.color[stream].value;

        if (
          localX === null &&
          localY === null &&
          localBlur === null &&
          localSpread === null &&
          localPosition === null &&
          localColor === null
        ) {
          return null;
        } else {
          const isText = value._target === 'text';
          const inset =
            isText || computedValue(value.position) !== 'inside' ? '' : 'inset';
          const x =
            CssUtils.renderValue({
              value: computedValue(value.x),
              unit: computedUnits(value.x),
            }) || '0';
          const y =
            CssUtils.renderValue({
              value: computedValue(value.y),
              unit: computedUnits(value.y),
            }) || '0';
          const blur =
            CssUtils.renderValue({
              value: computedValue(value.blur),
              unit: computedUnits(value.blur),
            }) || '0';
          const spread = !isText
            ? CssUtils.renderValue({
                value: computedValue(value.spread),
                unit: computedUnits(value.spread),
              }) || '0'
            : '';
          const color = computedValue(value.color);

          let joinedCss =
            inset +
            ' ' +
            x +
            ' ' +
            y +
            ' ' +
            blur +
            ' ' +
            (spread ? spread + ' ' : '') +
            color;
          joinedCss = trim(StringUtils.replaceAll(joinedCss, ' 0px', ' 0'));
          return joinedCss;
        }
      },
      defaultEmptyValue: 'none',
    });
  } else {
    return 'none';
  }
}

function formatTransitionParts(values: EffectTransition[]) {
  const CSS_TRANSITION = 'transition';
  const CSS_PROPERTY = CSS_TRANSITION + '-property';
  const CSS_DURATION = CSS_TRANSITION + '-duration';
  const CSS_TIMING = CSS_TRANSITION + '-timing-function';

  if (values.length > 0) {
    return [
      {
        path: 'property',
        cssProp: CSS_PROPERTY,
        reset: 'none',
        formatter: _deepFormatter('property'),
      },
      {
        path: 'duration',
        cssProp: CSS_DURATION,
        reset: '0ms',
        formatter: _deepFormatter('duration'),
      },
      {
        path: 'timing',
        cssProp: CSS_TIMING,
        reset: 'ease',
        formatter: _deepFormatter('timing'),
      },
    ].map(function (subvalue) {
      return {
        property: subvalue.cssProp,
        value: _collapseValue({
          values,
          formatter: subvalue.formatter,
          defaultEmptyValue: subvalue.reset,
        }),
      };
    });
  } else {
    return [{property: CSS_PROPERTY, value: 'none'}];
  }
}

type TransformPart = (typeof TRANSFORM_PARTS)[number];

function formatTransformParts(values: EffectTransform[]) {
  if (values.length > 0) {
    return _collapseValue({
      values,
      formatter: function (value, stream) {
        const localValues = TRANSFORM_PARTS.reduce<
          Record<TransformPart, string | null>
        >(
          (obj, part) => {
            obj[part] = CssUtils.renderValue({
              value: value[part][stream].value,
              unit: value[part][stream].unit,
            });
            return obj;
          },
          {} as Record<TransformPart, string | null>
        );

        // Update the part's cached CSS value
        if (TRANSFORM_PARTS.every((part) => localValues[part] === null)) {
          return null;
        } else {
          let joinedCss = null;

          if (
            localValues.rotateX !== null ||
            localValues.rotateY !== null ||
            localValues.rotateZ !== null
          ) {
            if (
              localValues.rotateX === '0deg' &&
              localValues.rotateY === '0deg'
            ) {
              joinedCss = 'rotate(' + (localValues.rotateZ || '0deg') + ')';
            } else {
              joinedCss =
                'rotateX(' +
                (localValues.rotateX || '0deg') +
                ') rotateY(' +
                (localValues.rotateY || '0deg') +
                ') rotateZ(' +
                (localValues.rotateZ || '0deg') +
                ')';
            }
          } else if (
            localValues.scaleX !== null ||
            localValues.scaleY !== null ||
            localValues.scaleZ !== null
          ) {
            if (
              localValues.scaleX === localValues.scaleY &&
              localValues.scaleZ === '1'
            ) {
              joinedCss = 'scale(' + (localValues.scaleX || '1') + ')';
            } else {
              joinedCss =
                'scale3d(' +
                (localValues.scaleX || '1') +
                ', ' +
                (localValues.scaleY || '1') +
                ', ' +
                (localValues.scaleZ || '1') +
                ')';
            }
          } else if (
            localValues.translateX !== null ||
            localValues.translateY !== null ||
            localValues.translateZ !== null
          ) {
            if (
              localValues.translateZ !== '0px' &&
              localValues.translateZ !== '0%'
            ) {
              joinedCss =
                'translate3d(' +
                (localValues.translateX || '0px') +
                ', ' +
                (localValues.translateY || '0px') +
                ', ' +
                (localValues.translateZ || '0px') +
                ')';
            } else {
              joinedCss =
                'translate(' +
                (localValues.translateX || '0px') +
                ', ' +
                (localValues.translateY || '0px') +
                ')';
            }
          } else if (localValues.skewX !== null || localValues.skewY !== null) {
            joinedCss =
              'skew(' +
              (localValues.skewX || '0deg') +
              ', ' +
              (localValues.skewY || '0deg') +
              ')';
          } else if (localValues.perspective !== null) {
            joinedCss = 'perspective(' + localValues.perspective + ')';
          }

          return joinedCss;
        }
      },
      cssPartSeparator: ' ',
      defaultEmptyValue: 'none',
    });
  } else {
    return 'none';
  }
}

// Single-part formatters

type StreamType = 'local' | 'upstream';

function formatBackgroundPart(value: any, stream: StreamType) {
  switch (value._type) {
    case 'background':
      return formatBackgroundImagePart(value, stream);
    case 'gradient':
      return formatBackgroundGradientPart(value, stream);
    case 'solidcolor':
      return formatBackgroundSolidColorPart(value, stream);
    default:
      throw new Error(`invalid background part type "${value._type}"`);
  }
}

function formatBackgroundImagePart(value: any, stream: StreamType) {
  return value.image[stream].value;
}

function formatBackgroundGradientPart(value: any, stream: StreamType) {
  const type = value.type[stream].value;
  const repeat = value.repeat[stream].value !== 'no-repeat';
  const stops = computedValue(value.stops) as ColorStop[];
  const renderedStops = (function () {
    const totalStops = stops.length;
    const stopsCss: string[] = [];

    each(stops, function (stop, index) {
      let stopCss = (function () {
        const color = computedValue(stop.color);
        const position = combinedPosition(stop.position);
        const hidden = false;

        if (color === null) {
          return null;
        }

        if (hidden) {
          return null;
        }

        const css = color + ' ' + (position != null ? position : '');
        return css.trim();
      })();

      if (!stopCss) return;

      if (index === 0 && endsWith(stopCss, ' 0%')) {
        stopCss = StringUtils.strLeft(stopCss, ' 0%');
      } else if (index + 1 === totalStops && endsWith(stopCss, ' 100%')) {
        stopCss = StringUtils.strLeft(stopCss, ' 100%');
      }

      stopsCss.push(stopCss);
    });

    const validCss = reject(stopsCss, function (css) {
      return !css || css === '';
    });
    const parts = map(validCss, function (css) {
      return {css, disabled: false};
    });
    const joined = CssUtils.joinParts(parts);

    return joined;
  })();

  let innerValue = '';
  const angle = Number(value.angle[stream].value);
  const angleUnit = value.angle[stream].unit ?? 'deg';
  const size = value.size[stream].value || 'farthest-corner';
  const position = value.position[stream].value || '50% 50%';

  if (type === 'linear') {
    innerValue = `${angle}${angleUnit}, ${renderedStops}`;
  } else if (type === 'radial') {
    innerValue = 'circle ' + size + ' at ' + position + ', ' + renderedStops;
  } else {
    return null;
  }

  const css =
    (repeat ? 'repeating-' : '') + type + '-gradient(' + innerValue + ')';
  return css;
}

function formatBackgroundSolidColorPart(value: any, stream: StreamType) {
  const color = value.color[stream].value;
  const css = 'linear-gradient(to bottom, ' + color + ',' + color + ')';
  return css;
}

// Helpers

function _collapseValue<T extends {hidden: StyleProperty<any>}>(options: {
  values: T[] | string;
  defaultEmptyValue: string;
  cssPartSeparator?: string;
  formatter?: (value: T, stream: StreamType) => string | null;
}): string | null {
  const values = options.values;

  if (typeof values === 'string') {
    if (containsCssCustomValue(values)) {
      return values;
    } else {
      throw new Error(
        `TypeError: 'values' parameter expects a CSS custom property. Current input: \x1b[31m${values}\x1b[0m is not a valid CSS raw string.`
      );
    }
  }

  const defaultEmptyValue =
    options.defaultEmptyValue !== 'undefined'
      ? options.defaultEmptyValue
      : null;
  const cssPartSeparator = options.cssPartSeparator || ',';

  const formatter: (value: T, stream: StreamType) => string | null =
    options.formatter ||
    function (value, stream) {
      const streamValue = (value as any)[stream];

      return (
        String(streamValue.value) +
        (streamValue.unit !== null ? streamValue.unit : '')
      );
    };

  /**
   * Refresh combined UPSTREAM value
   */
  const upstreamCssChunks: {css: string; hidden: any}[] = [];
  each(values, function (value) {
    const css = formatter(value, StreamTypes.UPSTREAM);

    if (css !== null) {
      upstreamCssChunks.push({
        css,
        hidden: computedValue(value.hidden),
      });
    }
  });

  /**
   * Refresh combined LOCAL value
   */
  const localCssChunks: {css: string; hidden: any}[] = [];
  each(values, function (value) {
    const css = formatter(value, StreamTypes.LOCAL);

    if (css !== null) {
      localCssChunks.push({
        css,
        hidden: computedValue(value.hidden),
      });
    }
  });

  // Determine if any of the parts have a local value
  // If one does, then we have to make the entire CSS prop local (because CSS sucks for multi-value inheritance)
  const upstreamSignature =
    map(upstreamCssChunks, function (chunk) {
      return chunk.hidden ? 'H' : 'V';
    }).join('-') +
    '(' +
    upstreamCssChunks.length +
    ')';

  const localSignature =
    map(localCssChunks, function (chunk) {
      return chunk.hidden ? 'H' : 'V';
    }).join('-') +
    '(' +
    localCssChunks.length +
    ')';

  const signatureMismatch = upstreamSignature !== localSignature;

  // Go through each part and put together the CSS chunks that make up the combined value
  const keepCssChunks: {css: string | null; hidden: any}[] = [];
  each(values, function (value) {
    let cssValue = formatter(value, StreamTypes.LOCAL);

    // If there is a signature mismatch between local and inherited parts (e.g. more parts or some are hidden),
    // then the local value has to come from upstream even though it isn't specified locally
    // E.g. 1 upstream background image gets a new image added, the details for the first image come from inherited
    // and the details for the first image are set locally
    if (cssValue === null && signatureMismatch && localCssChunks.length > 0) {
      const upstreamCssValue = formatter(value, StreamTypes.UPSTREAM);

      if (upstreamCssValue !== null) {
        cssValue = upstreamCssValue;
      }
    }

    if (cssValue !== null) {
      keepCssChunks.push({
        css: cssValue,
        hidden: computedValue(value.hidden),
      });
    } else {
      keepCssChunks.push({
        css: defaultEmptyValue,
        hidden: computedValue(value.hidden),
      });
    }
  });

  // Combine all parts into a comma-separated value
  const joinedCssValue = (function () {
    if (localCssChunks.length > 0) {
      return CssUtils.joinParts(keepCssChunks, {
        emptyValue: defaultEmptyValue,
        separator: cssPartSeparator,
      });
    } else {
      return null;
    }
  })();

  return joinedCssValue;
}

function _deepFormatter(path: string) {
  return function (value: any, stream: StreamType) {
    const subValue = value[path];

    if (subValue) {
      const v = subValue[stream].value;

      if (v !== null) {
        const u = subValue[stream].unit;

        return String(v) + (u !== null ? u : '');
      } else {
        return null;
      }
    } else {
      return null;
    }
  };
}
